/*!
  Pioneer WFRIA RadioButtonGroup v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["RadioButtonGroup"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("RadioButtonGroup") < 0) {
    window.__PIONEER_GLOBAL__.components.push("RadioButtonGroup");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
const _excluded = ["aria-label", "children", "className", "customStrings", "defaultSelectedIndex", "disabled", "id", "name", "onSelectedIndexChange", "required", "selectedIndex", "slim"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React, { forwardRef, useContext, useEffect, useRef, useState } from "react";
import { BUTTON, KEYBOARD_KEYS } from "../common/constants";
import { useControlOrUnControl, useFocusFieldOnError, useUniqueId } from "../common/hooks";
import { invalidIf, requiredIf } from "../common/proptypes";
import { registerField } from "../common/utilities";
import { FieldsetContext } from "../Fieldset/FieldsetContext";
import { FormContext } from "../Form/FormContext";
import FieldFooter from "../private/FieldFooter/FieldFooter";
import ScrollableContainer from "../private/ScrollableContainer/ScrollableContainer";
import extractTheme from "./extractTheme";
import STRINGS from "./strings";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

function RadioButtonGroupFooter(_ref) {
  let {
    displayErrors,
    errors,
    errorId,
    footerErrorStyle,
    label,
    name
  } = _ref;
  let fieldFooterProps = {};

  if (displayErrors && errors[name]) {
    fieldFooterProps = {
      displayErrors,
      label,
      footerErrorStyle,
      message: errors[name].message,
      id: errorId
    };
  }

  return _jsx(FieldFooter, _objectSpread({}, fieldFooterProps));
}
/**
 * RadioButtonGroup is a React component that is a more visually impactful form
 * element that displays mutually exclusive options.
 */


const RadioButtonGroup = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    "aria-label": ariaLabel,
    children,
    className,
    customStrings,
    defaultSelectedIndex,
    disabled,
    id,
    name,
    onSelectedIndexChange,
    required,
    selectedIndex: selectedIndexFromProps,
    slim
  } = props,
        otherProps = _objectWithoutProperties(props, _excluded);

  const theme = useTheme();
  const {
    cssRoot,
    radioButtonGroupContentCSS,
    radioButtonGroupLICSS,
    radioButtonGroupItemCSS,
    radioButtonGroupErrorCSS,
    selectedGroupItemCSS,
    silmButtonCSS,
    standardButtonCSS,
    radioButtonGroupLIErrorCSS
  } = extractTheme(_objectSpread({
    theme
  }, otherProps));
  const radioButtonGroupRef = useRef();
  const radioButtonGroupWrapperRef = useRef();
  const firstRadioButtonRef = useRef();
  const validateTimestampRef = useRef();
  const triggerId = useUniqueId({
    id,
    prefix: "pnr-radiobuttongroup-"
  });
  const {
    disabled: disabledFromFieldsetContext
  } = useContext(FieldsetContext) || {};
  const [isDisabled, setIsDisabled] = useState(disabled || disabledFromFieldsetContext);
  /**
   * Errors should be first shown on blur, and then on change.
   * This state "displayErrors" is used to that effect: even
   * if the Form is reporting errors, we do not show them
   * before onBlur is triggered and change this state.
   *
   * This state is also reverted back to false on reset.
   */

  const [displayErrors, setDisplayErrors] = useState(false);

  const componentStrings = _objectSpread(_objectSpread({}, STRINGS), customStrings);

  const {
    requiredErrorMessage
  } = componentStrings;
  const errorId = useUniqueId({
    prefix: "pnr-radio-button-group-error-"
  });
  const {
    register,
    isSubmitting,
    errors = {},
    resetDone,
    unregister,
    validateTimestamp
  } = useContext(FormContext) || {};
  useFocusFieldOnError(radioButtonGroupRef, firstRadioButtonRef, isSubmitting);
  /**
   * Registering our field with the "register" method from
   * react-hook-form.
   */

  const {
    inputRef,
    reactHookFormRest
  } = registerField({
    register,
    name,
    required,
    requiredErrorMessage
  });
  useEffect(() => {
    return () => {
      /**
       * Component is being unmounted.
       * Un-registering field with the "unregister" method
       * from react-hook-form.
       */
      unregister && unregister(name);
    };
  }, [unregister, name]);
  /**
   * Keep disabled state current with disabled prop changes.
   */

  useEffect(() => {
    setIsDisabled(disabled || disabledFromFieldsetContext);
  }, [disabled, disabledFromFieldsetContext]);
  /*
   * Reset RadioButton on React Hook Form reset.
   */

  useEffect(() => {
    if (resetDone) {
      setDisplayErrors(false);
      radioButtonGroupRef.current.removeAttribute("value");
    }
  }, [resetDone]);
  /**
   * useControlOrUnControl hook returns the getter and setter
   * methods for updating the prop value, renamed getPropValue to
   * getSelectedValue for sake of readability.
   */

  const [getSelectedItem, setSelectedItem] = useControlOrUnControl({
    unControlledPropValue: defaultSelectedIndex,
    controlledPropValue: selectedIndexFromProps,
    changeHandler: onSelectedIndexChange
  }); // Contains radio button items: native button nodes.

  const focusableElements = useRef([]);
  /**
   * Sets the reference for the Button components.
   */

  const setButtonRef = buttonNode => {
    // Set firstRadioButtonRef to first button for RHF focus on error.
    if (!firstRadioButtonRef.current) {
      firstRadioButtonRef.current = buttonNode;
    }

    focusableElements.current.indexOf(buttonNode) < 0 && buttonNode !== null && (focusableElements.current = focusableElements.current.concat(buttonNode));
  };
  /**
   * Handles the selection of radio button items.
   */


  const handleButtonSelect = _ref2 => {
    let {
      currentTarget
    } = _ref2;
    const radioGroupItemindex = parseInt(currentTarget.getAttribute("data-item"), 10); // Set selected Button index.

    setSelectedItem(radioGroupItemindex);
    radioButtonGroupRef.current.setAttribute("value", currentTarget.innerText);
    setDisplayErrors(false);
  };
  /**
   * Get the radio button item.
   *
   * @return {React.Element} Returns Button component
   */


  const getItem = (button, index) => {
    const {
      disabled: buttonDisabledProp
    } = button.props;
    const isButtonDisabled = buttonDisabledProp || isDisabled;
    const isSelected = getSelectedItem() === index;
    const itemProps = {};
    itemProps.kind = "standard";
    itemProps.onClick = handleButtonSelect;
    itemProps.role = "radio";
    itemProps["aria-checked"] = isSelected;
    itemProps["aria-disabled"] = isButtonDisabled;
    itemProps.disabled = isButtonDisabled;
    itemProps.slim = slim;
    itemProps.ref = isButtonDisabled ? null : setButtonRef;
    itemProps.css = _objectSpread(_objectSpread(_objectSpread({}, radioButtonGroupItemCSS), isSelected ? selectedGroupItemCSS : []), slim ? silmButtonCSS : standardButtonCSS);
    itemProps["data-item"] = index;
    itemProps.tabIndex = isSelected ? "0" : "-1";
    return /*#__PURE__*/React.cloneElement(button, itemProps);
  };

  const radioButtonGroupLIStyles = _objectSpread(_objectSpread({}, radioButtonGroupLICSS), errors[name] && displayErrors ? radioButtonGroupLIErrorCSS : []);
  /**
   * Get the radio button items.
   *
   * @return {React.Element} Returns Radio button group items
   */


  const getRadioButtonGroupItems = () => {
    return React.Children.map(children, (child, i) => {
      return _jsx("li", {
        role: "presentation",
        css: radioButtonGroupLIStyles,
        children: getItem(child, i)
      });
    });
  };
  /**
   * Gets the index of selected item w.r.t focusableElements array.
   * focusableElements holds only active elements.
   * In `Disabled` scenario, we cannot determine selected item position with only `selectedIndex`.
   * @return {Number} Returns the position of selected item in focusableElements.
   */


  const getSelectedElement = () => {
    const selectedIndex = getSelectedItem();

    for (let i = 0; i < focusableElements.current.length; i++) {
      if (parseInt(focusableElements.current[i].getAttribute("data-item"), 10) === selectedIndex) {
        return i;
      }
    }
  };
  /**
   * Method for assigning tabIndex for each RadioButtonGroupItem.
   * Only one item can hold focus at any moment of time.
   * Others will become nonfocusable through tabIndex `-1`.
   * @param {Number} currentFocusedElement The index of the focused element.
   */


  const assignTabIndex = index => {
    for (const element of focusableElements.current) {
      element && element.setAttribute("tabIndex", -1);
    } // The last focused radio button item should retain the tabIndex.


    focusableElements.current[index] && focusableElements.current[index].setAttribute("tabIndex", 0);
  };
  /**
   * Cycles the focus within the focusable items.
   */


  const cycleFocus = e => {
    const key = e.key;
    const len = focusableElements.current.length;
    let index = focusableElements.current.indexOf(e.target);

    if (index >= 0 && key === KEYBOARD_KEYS.UP || key === KEYBOARD_KEYS.DOWN || key === KEYBOARD_KEYS.LEFT || key === KEYBOARD_KEYS.RIGHT) {
      const dir = key === KEYBOARD_KEYS.DOWN || key === KEYBOARD_KEYS.RIGHT ? 1 : -1;
      index = (index + len + dir) % len;
      e.preventDefault();
      assignTabIndex(index);
      focusableElements.current[index] && focusableElements.current[index].focus();
    }
  };
  /**
   * Adds RadioButtonGroup keydown listener.
   */


  const handleKeydown = e => {
    cycleFocus(e);

    if (e.key === KEYBOARD_KEYS.TAB) {
      const elementToBeFocused = getSelectedElement() || 0;
      /**
       * Set focus on to the selected Button Item.
       * If none are selected, then set focus to the first focusable element.
       */

      setTimeout(() => {
        assignTabIndex(elementToBeFocused);
      }, 0);
    }
  };
  /**
   * Updates hidden input field state.
   */


  const updateHiddenInputField = e => {
    if (radioButtonGroupWrapperRef.current.contains(e.relatedTarget)) return; // Enable the underlying input to be operated on.

    radioButtonGroupRef.current.removeAttribute("hidden");
    radioButtonGroupRef.current.style.display = "inline";
    radioButtonGroupRef.current.focus();
    radioButtonGroupRef.current.blur(); // Return the underlying input to its former state.

    radioButtonGroupRef.current.style.display = "none";
    radioButtonGroupRef.current.setAttribute("hidden", true);
    /**
     * On blur, we need to show errors (if there are any).
     */

    !displayErrors && setDisplayErrors(true);
  }; // eslint-disable-next-line react-hooks/exhaustive-deps


  useEffect(() => {
    let currentSelectedIndex = getSelectedItem();

    if (selectedIndexFromProps === null && onSelectedIndexChange !== null) {
      currentSelectedIndex = selectedIndexFromProps;
      /**
       * When resetting the RBG, the `selectedIndex` should be assigned
       * a `null` value to its state, if not `getSelectedItem()` will
       * return its previous selected state value. The values of selectedIndex
       * and error will be updated when `selectedIndexFromProps` is null and
       * `onSelectedIndexChange` is not null.
       * Checking `selectedIndex` with current props and previous
       * props which prevents causing of infinite loop. `eslint` doesn't check
       * whether `setState` is wrapped inside a condition, therefore it throws
       * an error. Hence, disabling the `eslint` rule.
       */
      // eslint-disable-next-line react/no-did-update-set-state

      setSelectedItem(currentSelectedIndex);
    } // set tabIndex to 0 for the first radio button item, by ignoring any disabled item.


    if (!currentSelectedIndex && !errors[name] || currentSelectedIndex < 0) {
      focusableElements.current[0] && focusableElements.current[0].setAttribute("tabIndex", 0);
    }

    if (currentSelectedIndex > 0) {
      // Set tabIndex of RBG item on selection.
      const selectedElem = getSelectedElement();
      assignTabIndex(selectedElem);
    }
  });
  useEffect(() => {
    const selectedItem = getSelectedItem();
    radioButtonGroupRef.current.value = selectedItem > -1 && selectedItem !== null ? selectedItem : "";
  });
  useEffect(() => {
    if (validateTimestamp && validateTimestamp !== validateTimestampRef.current) {
      validateTimestampRef.current = validateTimestamp;
      /**
       * A manual validation was triggered at the form level.
       * That means we need to show error on field even if it's not
       * dirty (blurred) yet.
       */

      if (errors[name]) {
        !displayErrors && setDisplayErrors(true);
      }
    }
  }, [displayErrors, errors, name, validateTimestamp]);
  /**
   * Show errors if the Form is being submitted even if the field
   * hasn't been blurred yet.
   */

  useEffect(() => {
    isSubmitting && setDisplayErrors(true);
  }, [isSubmitting]);
  return _jsxs(React.Fragment, {
    children: [_jsx(ScrollableContainer, {
      children: _jsxs("div", {
        className: className,
        css: cssRoot,
        ref: radioButtonGroupWrapperRef,
        children: [_jsx("ul", _objectSpread(_objectSpread({
          role: "radiogroup",
          onKeyDown: handleKeydown,
          css: radioButtonGroupContentCSS,
          "aria-disabled": isDisabled,
          "aria-describedby": required && displayErrors ? errorId : null,
          "aria-label": ariaLabel,
          onBlur: required ? updateHiddenInputField : null
        }, otherProps), {}, {
          id: triggerId,
          children: getRadioButtonGroupItems()
        })), _jsx("input", _objectSpread(_objectSpread({
          className: "wf-u-visually-hidden",
          ref: e => {
            /**
             * Normally, react-hook-form does that automatically.
             * In this case, we want to share the ref with the user, so
             * we need to run this ourselves.
             *
             * See "How to share ref usage?" in the FAQs.
             * https://react-hook-form.com/faqs
             */
            inputRef && inputRef(e);
            radioButtonGroupRef.current = e;

            if (ref) {
              ref.current = e;
            }
          },
          name: name
        }, reactHookFormRest), {}, {
          required: required,
          hidden: true
        }))]
      })
    }), _jsx(RadioButtonGroupFooter, {
      displayErrors: displayErrors,
      errors: errors,
      errorId: errorId,
      label: ariaLabel,
      name: name,
      footerErrorStyle: radioButtonGroupErrorCSS
    })]
  });
});
RadioButtonGroup.propTypes = process.env.NODE_ENV !== "production" ? {
  /** The name for the RadioButtonGroup. */
  name: PropTypes.string.isRequired,

  /** aria-label for the RadioButtonGroup. */
  "aria-label": requiredIf(PropTypes.string, (props, propName) => !props["aria-labelledby"] && !props[propName], "if `aria-labelledby` is not configured"),

  /** aria-labelledby for the RadioButtonGroup. */
  "aria-labelledby": PropTypes.string,

  /**
   * RadioButtonGroup accepts only [Button](#/Components/Button) as children.
   */
  children: invalidIf(PropTypes.node, (props, propName) => {
    let error = false;
    const prop = props[propName];
    React.Children.map(prop, child => {
      const childrenType = child.type;

      if (childrenType.displayName !== BUTTON) {
        error = true;
      }
    });
    return error;
  }, `'RadioButtonGroup' children should be of type '${BUTTON}'.`),

  /** @ignore Class name for the root element. */
  className: PropTypes.string,

  /** Override for strings used within the component. */
  customStrings: PropTypes.shape({
    /** Error message to be displayed when a required field is invalid. */
    requiredErrorMessage: PropTypes.string
  }),

  /**
   * Indicates the default index of the RadioButtonGroup item
   * that should be in the selected state.
   * Its value should be between 0 and the number of `RadioButtonGroup`
   * items passed via `children` prop minus one.
   */
  defaultSelectedIndex: PropTypes.number,

  /** Disable or enable the look of a Radio button group. */
  disabled: PropTypes.bool,

  /**
   * Callback triggered when the `selectedIndex` changes.
   * @param {Number} index The index of the selected RadioButtonGroup item.
   */
  onSelectedIndexChange: PropTypes.func,

  /** Indicates if at least one item in the Radio Button Group should be selected. */
  required: PropTypes.bool,

  /**
   * Indicates the index of the RadioButtonGroup item
   * that should be in the selected state.
   * When using this prop, the component will behave as a __Controlled__ component
   * which requires the `onSelectedIndexChange` prop to be set
   * to sync the `selectedIndex` back.
   */
  selectedIndex: PropTypes.number,

  /** Renders the RadioButtonGroup with slim buttons. */
  slim: PropTypes.bool
} : {};
RadioButtonGroup.defaultProps = {
  "aria-label": null,
  "aria-labelledby": null,
  children: null,
  className: null,
  customStrings: {},
  defaultSelectedIndex: null,
  disabled: false,
  onSelectedIndexChange: null,
  required: false,
  selectedIndex: null,
  slim: false
};
RadioButtonGroup.displayName = "RadioButtonGroup";
export default RadioButtonGroup;